import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { EventAwardRow } from '../hooks/useEventManagement';

/**
 * 賞品の授与の発表順を、行のドラッグで並べ替えるダイアログ。
 *
 * - イベントの全行を今の発表順で縦に並べ、つまみ (左端) をドラッグして動かす。キーボードはつまみで Space → ↑↓ → Space
 * - 「保存する」で発表順を 1 から振り直す。キャンセルすれば何も変わらない
 * - 一覧の絞り込み・検索・ページ送りに関係なく、全行を対象にする
 */

const personLabel = (a: EventAwardRow): string => `${a.last_name ?? ''} ${a.first_name ?? ''}`.trim();

/** 並べ替えの 1 行 (つまみをドラッグして動かす) */
function SortableAwardRow({ award, index }: { award: EventAwardRow; index: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: award.id });
  const moved = award.sort_order !== index + 1;
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        bgcolor: isDragging ? '#f8fafc' : '#fff',
        border: 1,
        borderColor: isDragging ? '#93c5fd' : 'divider',
        borderRadius: 1,
        boxShadow: isDragging ? '0 4px 12px rgba(15,23,42,0.15)' : 'none',
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`${award.award_name} を動かす`}
        sx={{ display: 'flex', cursor: isDragging ? 'grabbing' : 'grab', color: '#94a3b8', touchAction: 'none' }}
      >
        <Icons.DragIndicator sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ width: 44, flexShrink: 0, textAlign: 'right' }}>
        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700 }}>
          {index + 1}
        </Typography>
        {moved && award.sort_order != null && (
          <Typography component="span" sx={{ fontSize: '10px', color: '#94a3b8', ml: 0.5 }}>
            ({award.sort_order})
          </Typography>
        )}
      </Box>
      <Chip
        size="small"
        label={award.award_type}
        sx={{
          fontSize: '11px',
          height: '20px',
          fontWeight: 700,
          flexShrink: 0,
          ...(award.award_type === '表彰' ? { bgcolor: '#eff6ff', color: '#3b82f6' } : { bgcolor: '#fffbeb', color: '#d97706' }),
        }}
      />
      <Typography sx={{ fontSize: '13px', fontWeight: 700, width: 120, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {award.award_name}
      </Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 0.5,
          overflow: 'hidden',
          bgcolor: award.image_url ? 'transparent' : '#f1f5f9',
        }}
      >
        {award.image_url && (
          <Box component="img" src={award.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </Box>
      <Typography sx={{ fontSize: '13px', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {award.prize_name ?? ''}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          width: 110,
          flexShrink: 0,
          textAlign: 'right',
          color: award.participant_id == null ? '#94a3b8' : '#334155',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {award.participant_id == null ? '当選者未定' : `${personLabel(award)} 様`}
      </Typography>
    </Box>
  );
}

const EventAwardOrderDialog = React.memo(function EventAwardOrderDialog({
  open,
  awards,
  onClose,
  onSave,
}: {
  open: boolean;
  // イベントの全行 (今の発表順に並んだもの)
  awards: EventAwardRow[];
  onClose: () => void;
  onSave: (awardIds: number[]) => Promise<boolean>;
}) {
  const [order, setOrder] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  // 開いたときに今の並びで初期化する (開いている間に一覧が再取得されても並べ替え中の順は保つ)
  useEffect(() => {
    if (open) setOrder(awards.map((a) => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const awardById = useMemo(() => new Map(awards.map((a) => [a.id, a])), [awards]);
  const rows = order.map((id) => awardById.get(id)).filter((a): a is EventAwardRow => !!a);
  // 今の発表順 (1 から連番) と違う行の数。番号が飛んでいるだけの行も振り直しの対象になる
  const changedCount = rows.filter((a, i) => a.sort_order !== i + 1).length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrder((prev) => arrayMove(prev, prev.indexOf(Number(active.id)), prev.indexOf(Number(over.id))));
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(order);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.Sort sx={{ fontSize: 18 }} />
        発表順を並べ替え
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          左端のつまみをドラッグして、発表する順に並べてください。保存すると発表順を 1 から振り直します。
          (括弧内は今の発表順)
        </Typography>
        <Box sx={{ maxHeight: '62vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5, pr: 0.5 }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              {rows.map((a, i) => (
                <SortableAwardRow key={a.id} award={a} index={i} />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mr: 'auto' }}>
          {rows.length} 件{changedCount > 0 ? `／発表順が変わる行 ${changedCount} 件` : ''}
        </Typography>
        <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || changedCount === 0}
          startIcon={saving ? <CircularProgress size={14} /> : <Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EventAwardOrderDialog;
